import { useState, useCallback } from "react";
import { AlertType } from "../components/AlertDialog";

interface AlertDialogState {
  open: boolean;
  title?: string;
  message: string;
  type: AlertType;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  onConfirm?: () => void;
}

export const useAlertDialog = () => {
  const [dialogState, setDialogState] = useState<AlertDialogState>({
    open: false,
    message: "",
    type: "info",
  });

  const showAlert = useCallback(
    (
      message: string,
      type: AlertType = "info",
      title?: string,
      confirmText?: string,
      onConfirm?: () => void
    ) => {
      setDialogState({
        open: true,
        message,
        type,
        title,
        confirmText,
        onConfirm,
      });
    },
    []
  );

  const showConfirmDialog = useCallback(
    (
      message: string,
      title?: string,
      confirmText = "Confirm",
      cancelText = "Cancel",
      onConfirm?: () => void
    ) => {
      setDialogState({
        open: true,
        message,
        type: "warning",
        title,
        confirmText,
        cancelText,
        showCancel: true,
        onConfirm,
      });
    },
    []
  );

  const showSuccess = useCallback(
    (message: string, title?: string) => {
      showAlert(message, "success", title);
    },
    [showAlert]
  );

  const showError = useCallback(
    (message: string, title?: string) => {
      showAlert(message, "error", title);
    },
    [showAlert]
  );

  const showWarning = useCallback(
    (message: string, title?: string) => {
      showAlert(message, "warning", title);
    },
    [showAlert]
  );

  const showInfo = useCallback(
    (message: string, title?: string) => {
      showAlert(message, "info", title);
    },
    [showAlert]
  );

  const closeDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    dialogState,
    showAlert,
    showConfirmDialog,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeDialog,
  };
};
